import { useEffect, useState } from "react";
import api from "@/lib/api";

const TABS = [
  ["pending_review", "Pending Review"],
  ["approved", "Approved"],
  ["changes_requested", "Changes Requested"],
  ["rejected", "Rejected"],
  ["suspended", "Suspended"],
  ["all", "All"],
];

export default function AdminListings() {
  const [tab, setTab] = useState("pending_review");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  const load = (t = tab) => {
    setLoading(true);
    api.get(`/admin/listings?status=${t}`).then(r => setListings(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const act = async (action) => {
    setErr("");
    if (["reject", "suspend", "request_changes"].includes(action) && !reason.trim()) {
      return setErr("A reason is required");
    }
    try {
      await api.post(`/admin/listings/${selected.id}/action`, { action, reason });
      setSelected(null); setReason(""); load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      <h1 className="font-display text-3xl font-semibold text-[var(--fpn-navy)]" data-testid="admin-listings-title">Community Listings</h1>

      <div className="flex flex-wrap gap-2 mt-6">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm border ${tab === k ? "bg-[var(--fpn-navy)] text-white border-[var(--fpn-navy)]" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {!loading && listings.length === 0 && <div className="p-6 text-slate-500" data-testid="admin-listings-empty">No listings in this tab.</div>}
        <div className="grid gap-3">
          {listings.map(l => (
            <button key={l.id} onClick={() => { setSelected(l); setReason(""); setErr(""); }}
              className="text-left border border-slate-200 rounded-2xl p-5 bg-white hover:border-[var(--fpn-navy)] transition-colors flex justify-between items-center flex-wrap gap-3"
              data-testid={`admin-listing-row-${l.id}`}>
              <div>
                <div className="font-semibold text-[var(--fpn-navy)]">{l.title || "(Untitled)"}</div>
                <div className="text-sm text-slate-500">
                  {l.airport_code} · £{l.base_daily_price}/day · capacity {l.capacity} · {l.host?.email || "host"}
                </div>
              </div>
              <span className="text-[11px] font-bold px-2 py-1 rounded-md tracking-widest bg-slate-100 text-slate-700 uppercase">{l.status.replace("_", " ")}</span>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-review-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-display text-2xl font-semibold text-[var(--fpn-navy)]" data-testid="admin-review-title">{selected.title}</h2>
                <div className="text-sm text-slate-500 mt-1">{selected.airport_code} · {selected.space_type} · Capacity {selected.capacity}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500" data-testid="admin-review-close">✕</button>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">Host: </span>{selected.host?.first_name} {selected.host?.last_name} ({selected.host?.email})</div>
              <div><span className="text-slate-500">Address: </span>{selected.address_line}, {selected.city}, {selected.postcode}</div>
              <div><span className="text-slate-500">Price: </span>£{selected.base_daily_price}/day</div>
              <div><span className="text-slate-500">Features: </span>{(selected.features || []).join(", ")}</div>
            </div>
            <p className="text-sm text-slate-700 mt-4">{selected.description}</p>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {(selected.images || []).slice(0, 3).map((img, i) => <img key={i} src={img} alt="" className="w-full h-24 object-cover rounded-lg"/>)}
            </div>

            <div className="mt-6">
              <label className="text-xs font-bold tracking-widest uppercase text-slate-500">Reason (required for reject / suspend / request changes)</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg p-3 text-sm mt-2" data-testid="admin-review-reason"/>
              {err && <div className="text-sm text-red-600 mt-2" data-testid="admin-review-error">{err}</div>}
            </div>

            <div className="flex flex-wrap gap-2 justify-end mt-6">
              <button onClick={() => act("request_changes")} className="rounded-full px-4 py-2 text-sm border border-slate-200" data-testid="admin-btn-changes">Request Changes</button>
              <button onClick={() => act("reject")} className="rounded-full px-4 py-2 text-sm border border-red-200 text-red-700" data-testid="admin-btn-reject">Reject</button>
              <button onClick={() => act("suspend")} className="rounded-full px-4 py-2 text-sm border border-slate-200 text-slate-700" data-testid="admin-btn-suspend">Suspend</button>
              <button onClick={() => act("approve")} className="btn-amber rounded-full px-4 py-2 text-sm" data-testid="admin-btn-approve">Approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
